import { Component, OnChanges, OnDestroy, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonInfiniteScroll } from '@ionic/angular';
import { Subscription, Observable } from 'rxjs';
import 'rxjs/add/observable/of';

import { SHORT_LIST_MAX_MOVEMENTS, TransferTypes } from '@constants/miscellaneous';
import { MovementInfoTypeCollection } from '@interfaces/movement-info';
import {
  IDeposit,
  IWithdrawal,
  IPassiveMovement,
  IEnrichedListMovement,
  IMovement,
  MovementListParentType,
  PARENT_TYPE_TO_FIELD_MAP,
  WithdrawalStatus,
  OrderOperation,
} from '@interfaces/movements';
import { MovementsService } from '@services/movement/movement.service';
import Utils from '@util/util';

const groupLoad = 10;
const operationToMovementTypeCollection = {
  'withdrawal': MovementInfoTypeCollection.WITHDRAWAL,
  'contribution': MovementInfoTypeCollection.DEPOSIT,
};

@Component({
  selector: 'app-investment-movement-list',
  templateUrl: './investment-movement-list.component.html',
  styleUrls: ['./investment-movement-list.component.scss'],
})
export class InvestmentMovementListComponent implements OnChanges, OnDestroy {
  @ViewChild(IonInfiniteScroll, {static: false}) infiniteScroll: IonInfiniteScroll;
  @Input() inMiddleColumn: boolean;
  @Input() selected: string;
  @Input() uid: string;
  @Input() parentId: string;
  @Input() parentType: MovementListParentType;
  @Input() displayFilters: boolean;
  @Input() isUSD: boolean;
  @Input() assetId?: string;
  @Input() oldTickers?: string[];
  @Input() filterBy?: string;
  @Input() movementsTitle?: boolean;
  @Input() hideTitleWhenNoMovements?: boolean;
  @Input() isStocksSummary?: boolean;
  @Output() hasMovements = new EventEmitter<number>();
  @Output() loadDividends = new EventEmitter<number>();

  subscription: Subscription;
  isDataLoaded: boolean;
  movementList: IEnrichedListMovement[];
  displayMovements: IEnrichedListMovement[];
  inProgressMovements: IEnrichedListMovement[];
  movements: {
    contributions: IDeposit[],
    withdrawals: IWithdrawal[],
    dividends: IPassiveMovement[],
    interests: IPassiveMovement[],
    rebatesInterests: IPassiveMovement[],
    commissions: IPassiveMovement[],
    buys: IDeposit[],
    sells: IWithdrawal[],
    corporateEvents: IPassiveMovement[],
  };
  movementFilters:  {
    noneSelected: boolean,
    contributions: boolean,
    withdrawals: boolean,
    dividends: boolean,
    interests: boolean,
    rebatesInterests: boolean,
    commissions: boolean,
    buys: boolean,
    sells: boolean,
    corporateEvents: boolean,
  };
  displayCount: number;

  constructor(
    public route: ActivatedRoute,
    private movementService: MovementsService,
    private router: Router,
  ) {
    this.isDataLoaded = false;
    this.movementList = [];
    this.movementFilters = {
      noneSelected: true,
      contributions: false,
      withdrawals: false,
      dividends: false,
      interests: false,
      rebatesInterests: false,
      commissions: false,
      buys: false,
      sells: false,
      corporateEvents: false,
    };
    this.displayCount = 0;
  }

  ngOnChanges() {
    this.isDataLoaded = false;
    const parentField = PARENT_TYPE_TO_FIELD_MAP[this.parentType];
    this.subscription = Observable.combineLatest([
      this.parentType === MovementListParentType.Users ?
        this.movementService.getDepositsById(this.parentId, parentField) :
        this.movementService.getContributionsById(this.parentId, parentField, this.uid, this.isUSD),
      this.movementService.getWithdrawalsById(this.parentId, parentField, this.uid || this.parentId, this.isUSD),
      // Dividends: merge DW (API) + CLP (Firebase) for users, or get portfolio-specific
      this.parentType === MovementListParentType.Users ?
        this.movementService.getAllUserDividends(this.parentId, this.assetId) :
        this.isUSD && this.parentType === MovementListParentType.Portfolios ?
          this.movementService.getPortfoliosDividendsFromAPI(this.parentId, this.uid, this.assetId) :
          this.movementService.getMovementCollectionByParentId(this.parentType, this.parentId, 'dividends'),
      this.movementService.getMovementCollectionByParentId(this.parentType, this.parentId, 'interests'),
      this.movementService.getMovementCollectionByParentId(this.parentType, this.parentId, 'commissions'),
      this.movementService.getPaidKhipuTransfers(this.uid || this.parentId, this.parentType, this.parentId),
      // Corporate events: API-only for users and USD portfolios, empty for CLP
      (this.parentType === MovementListParentType.Users || this.isUSD) ?
        this.movementService.getUserCorporateEventsFromAPI(this.uid || this.parentId, this.assetId) :
        Observable.of([]),
    ])
    .subscribe((
      [contributions, withdrawals, dividends, interests, commissions, khipuTransfers, corporateEvents]:
      [IDeposit[], IWithdrawal[], IPassiveMovement[], IPassiveMovement[], IPassiveMovement[], IDeposit[], IPassiveMovement[]],
     ) => {
      if (contributions && withdrawals && dividends && interests && commissions && khipuTransfers && corporateEvents) {
        const buys = contributions.filter(d => d.isBuy);
        const deposits = contributions.filter(d  => !d.isBuy);
        const sells = withdrawals.filter(s => s.isSell);
        const onlyWithdrawals = withdrawals.filter(s => !s.isSell);
        const rebatesInterests = interests.filter(i => i.isRebateInterest);
        const walletinterests = interests.filter(i => !i.isRebateInterest);
        this.movements = { contributions: [...deposits, ...khipuTransfers], withdrawals: onlyWithdrawals, dividends,
                           interests: walletinterests, rebatesInterests, commissions, buys, sells,
                           corporateEvents };
        // Filter by asset if assetId is provided (both USD and CLP)
        if (this.assetId) {
          this.filterByAsset();
        }
        this.renderFilteredMovementsByYear();
        this.hasMovements.emit(this.displayCount);
        if (this.filterBy) {
          this.toggle(this.filterBy);
        }
        this.isDataLoaded = true;
      }
      if (this.assetId && this.isUSD) {
        this.calculateDividends();
      }
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  filterByAsset() {
    this.movements.withdrawals = this.movements.withdrawals.filter(withdrawal => withdrawal.uniqueAssetId === this.assetId);
    this.movements.dividends = this.movements.dividends.filter(dividend => dividend.assetId === this.assetId);
    this.movements.commissions = [];
    this.movements.interests = [];
    this.movements.rebatesInterests = [];
    this.movements.contributions = this.movements.contributions.filter((contribution) => {
      const contributionAsset = (contribution.executedShareVariations && contribution.executedShareVariations[0].assetId) ||
                       (contribution.proposedShareVariations && contribution.proposedShareVariations[0].assetId) ||
                       (contribution.contributionStrategy && contribution.contributionStrategy.uniqueAssetId);
      return contributionAsset === this.assetId || (this.oldTickers && this.oldTickers.includes(contributionAsset));
    });
    this.movements.buys = this.movements.buys.filter((buy) => {
      const buyAsset = (buy.executedShareVariations && buy.executedShareVariations[0].assetId) ||
                       (buy.proposedShareVariations && buy.proposedShareVariations[0].assetId) ||
                       (buy.contributionStrategy && buy.contributionStrategy.uniqueAssetId);
      return buyAsset === this.assetId || (this.oldTickers && this.oldTickers.includes(buyAsset));
    });
    this.movements.sells = this.movements.sells.filter(sell => sell.uniqueAssetId === this.assetId);
    this.movements.corporateEvents = this.movements.corporateEvents.filter(event => event.assetId === this.assetId);
  }

  renderFilteredMovementsByYear() {
    if (this.infiniteScroll) {
      this.infiniteScroll.disabled = false;
    }
    let movements;
    if (!this.displayFilters || this.movementFilters.noneSelected) {
      movements = [].concat(
        this.movements.contributions,
        this.movements.withdrawals,
        this.movements.dividends,
        this.movements.interests,
        this.movements.rebatesInterests,
        this.movements.commissions,
        this.movements.buys,
        this.movements.sells,
        this.movements.corporateEvents,
      );
    } else {
      movements = [
        ...(this.movementFilters.contributions ? this.movements.contributions : []),
        ...(this.movementFilters.withdrawals ? this.movements.withdrawals : []),
        ...(this.movementFilters.dividends ? this.movements.dividends : []),
        ...(this.movementFilters.interests ? this.movements.interests : []),
        ...(this.movementFilters.rebatesInterests ? this.movements.rebatesInterests : []),
        ...(this.movementFilters.commissions ? this.movements.commissions : []),
        ...(this.movementFilters.buys ? this.movements.buys : []),
        ...(this.movementFilters.sells ? this.movements.sells : []),
        ...(this.movementFilters.corporateEvents ? this.movements.corporateEvents : []),
      ];
    }
    // Sort by createdAt.seconds (Firebase timestamp format) - newest first
    let sortedMovements = movements.sort((a, b) => {
      const aTime = a.createdAt && a.createdAt.seconds ? a.createdAt.seconds : 0;
      const bTime = b.createdAt && b.createdAt.seconds ? b.createdAt.seconds : 0;
      return bTime - aTime; // Descending order (newest first)
    }) as IEnrichedListMovement[];
    if (!this.displayFilters) {
      this.inProgressMovements = sortedMovements.filter(m => m.status !== WithdrawalStatus.Complete && !m.isPassive);
      this.inProgressMovements = this.inProgressMovements.map(this.enrichMovement);
      sortedMovements = sortedMovements.filter(m => !(m.status !== WithdrawalStatus.Complete && !m.isPassive));
      sortedMovements.splice(SHORT_LIST_MAX_MOVEMENTS);
    }
    sortedMovements = sortedMovements.map(this.enrichMovement);
    this.movementList = [];
    for (let index = 0; index < sortedMovements.length; index++) {
      const element = sortedMovements[index];
      element.year = new Date(element.createdAt['seconds'] * 1000).getFullYear().toString();
      this.movementList.push(element);
    }
    this.displayCount = sortedMovements.length;
    this.displayMovements = this.movementList.slice(0, groupLoad);
  }

  toggle(category) {
    this.movementFilters[category] = !this.movementFilters[category];
    this.movementFilters.noneSelected =
      !this.movementFilters.contributions &&
      !this.movementFilters.withdrawals &&
      !this.movementFilters.dividends &&
      !this.movementFilters.interests &&
      !this.movementFilters.rebatesInterests &&
      !this.movementFilters.commissions &&
      !this.movementFilters.buys &&
      !this.movementFilters.sells &&
      !this.movementFilters.corporateEvents;
    this.renderFilteredMovementsByYear();
  }

  enrichMovement = (movement: IMovement): IEnrichedListMovement => {
    const enrichedMovement = {...movement} as IEnrichedListMovement;
    this.addMovementRouterLinkAndParams(enrichedMovement, this.parentType);

    // For corporate events, use amountUSD
    const passiveMovement = movement as IPassiveMovement;
    if (passiveMovement.isCorporateEvent) {
      enrichedMovement.amountToShow = movement.amountUSD || 0;
      enrichedMovement.displayUSD = true;
      return enrichedMovement;
    }

    // For DW dividends (from API), use amountUSD
    const isDWDividend = passiveMovement.isDWDividend
      || (movement.isPassive && movement.typeClass === 'dividends' && movement.amountUSD && !movement.amount);

    if (isDWDividend) {
      enrichedMovement.amountToShow = movement.amountUSD;
      enrichedMovement.displayUSD = true;
    } else {
      enrichedMovement.amountToShow =
        (movement.operation === OrderOperation.Withdrawal
          && (movement.status === WithdrawalStatus.Complete || movement.status === WithdrawalStatus.Executed))
        ? movement.amount
        : movement.executedAmount
          ? movement.executedAmount
          : movement.finalDepositAmount
            ? movement.finalDepositAmount
            : movement.amount;
      if (this.isUSD) {
        if (movement.operation === OrderOperation.Withdrawal) {
          enrichedMovement.amountToShow = (movement.status === WithdrawalStatus.Complete || movement.status === WithdrawalStatus.Executed)
            ? movement.finalWithdrawalAmountUSD
            : movement.amountUSD;
        } else {
          enrichedMovement.amountToShow = movement.executedAmountUSD || movement.proposedAmountUSD || movement.amountUSD;
        }
      }
      enrichedMovement.displayUSD = movement.amountUSD && !movement.amount;
    }
    if (movement.isStockTransfer) {
      enrichedMovement.type = 'Recibiste ' + (this.assetId || 'Stocks');
    }
    return enrichedMovement;
  }

  showMoreMovements(event) {
    if (this.displayMovements.length < this.movementList.length) {
      this.displayMovements.push(...this.movementList.slice(this.displayMovements.length, this.displayMovements.length + groupLoad));
      setTimeout(() => {
        this.infiniteScroll.complete();
      });
    } else {
      event.target.complete();
      this.infiniteScroll.disabled = true;
    }
  }

  calculateDividends() {
    let dividendsAmount = 0;
    this.movements && this.movements.dividends.forEach(d => {
      if (d.amountUSD) {
        dividendsAmount += d.amountUSD;
      }
    });
    this.loadDividends.emit(dividendsAmount);
  }

  goToAllMovements() {
    const queryParams = { parentId: this.parentId, parentType: this.parentType };
    this.router.navigate(['/filter-movements'], { queryParams });
  }

  private shouldUseNewMovementPage(movement: IEnrichedListMovement): boolean {
    return movement.transferType && movement.transferType === TransferTypes.TRANSFER_FROM_PORTFOLIO_TO_STOCKS;
  }

  private addNewMovementPageParams(movement: IEnrichedListMovement): void {
    movement.routerLink = ['/movement'];
    movement.queryParams = {
      type: operationToMovementTypeCollection[movement.operation],
      movementId: movement.id,
    };
  }

  private addLegacyMovementPageParams(movement: IEnrichedListMovement, parentType: MovementListParentType): void {
    const isContributionFromPortfolios = parentType === MovementListParentType.Portfolios
      && movement.operation === OrderOperation.Contribution;
    movement.routerLink = [
      `/movements/${movement.isPassive ? 'passive/' : ''}`,
      isContributionFromPortfolios ? movement.orderId : movement.id,
    ];
    movement.queryParams = {
      type: movement.operation,
      amount: '' + (parentType === MovementListParentType.Portfolios ? 0 : movement.amountToShow),
      referral: (movement.referral || movement.referred) ? 'true' : 'false',
      status: movement.status,
      onlyCashExcess: movement.onlyCashExcess ? 'true' : 'false',
      dwPortfolioId: (parentType === MovementListParentType.Portfolios && !this.assetId && movement.amountUSD) ? this.parentId : undefined,
    };
  }

  addMovementRouterLinkAndParams(movement: IEnrichedListMovement, parentType: MovementListParentType): void {
    const passiveMovement = movement as any as IPassiveMovement;
    if (passiveMovement.isCorporateEvent) {
      movement.routerLink = ['/movements/corporate-event/', movement.id];
      movement.queryParams = { actionType: passiveMovement.actionType };
      return;
    }
    if (this.shouldUseNewMovementPage(movement)) {
      this.addNewMovementPageParams(movement);
    } else {
      this.addLegacyMovementPageParams(movement, parentType);
    }
  }
}
